import { safeEvaluate } from '../utils/calculations';
import dndStructureData from '../data/full_dnd_structure.json';

// Types moved here for better encapsulation
export interface DndArchetype {
  key: string;
  name: string;
  emoji: string;
  priority: number;
  criteria: string;
  description: string;
  subpersonalities?: any[];
}

export interface DndStructure {
  archetypes: Record<string, DndArchetype>;
  alignment?: {
    external?: any[];
    internal?: any[];
  };
  external?: any[];
  internal?: any[];
}

export interface ClusterInfo {
  name: string;
  description: string;
  emoji: string;
  priority: number;
  subpersonalities: any[];
}

export const DEFAULT_ARCHETYPE: DndArchetype = {
  key: 'wanderer',
  name: 'Странник',
  emoji: '🌫',
  priority: 100,
  criteria: 'true',
  description: 'Нейтральный тип, без выраженного паттерна.',
  subpersonalities: []
};

export const DEFAULT_CLUSTER: ClusterInfo = {
  name: 'Неизвестный',
  description: 'Архетип не определен',
  emoji: '❓',
  priority: 999,
  subpersonalities: []
};

export const ERROR_CLUSTER: ClusterInfo = {
  name: 'Ошибка',
  description: 'Ошибка при расчете архетипа',
  emoji: '⚠️',
  priority: 999,
  subpersonalities: []
};
/**
 * Service for handling DnD archetype calculations
 */
class DndService {
  private structure: DndStructure;

  constructor(structure?: DndStructure) {
    this.structure = structure || this.loadStructure();
  }

  /**
   * Load DnD structure from various sources
   */
  private loadStructure(): DndStructure {
    try {
      // Try to get from window (browser)
      if (typeof window !== 'undefined' && (window as any).dndStructure) {
        return (window as any).dndStructure;
      }
      
      // Fallback to imported data
      return dndStructureData as unknown as DndStructure;
    } catch (error) {
      console.error('[DndService] Failed to load DnD structure:', error);
      // Return minimal structure with default archetype
      return {
        archetypes: {
          [DEFAULT_ARCHETYPE.key]: DEFAULT_ARCHETYPE
        }
      };
    }
  }

  /**
   * Calculate DnD archetype based on scores and extended metrics
   */
  public calculateDndArchetype(
    scores: Record<string, number>,
    extendedMetrics: Record<string, any> = {}
  ): DndArchetype | null {
    try {
      if (!scores || typeof scores !== 'object') {
        console.warn('[DndService] Invalid scores parameter');
        return null;
      }

      if (!this.structure?.archetypes) {
        console.warn('[DndService] Invalid DnD structure');
        return null;
      }

      // Create scope with all available data and ensure all required variables are defined
      const scope = {
        // Use only the original keys from scores and extendedMetrics (no case conversion)
        ...scores,
        ...extendedMetrics
      };
      // DEBUG: log the final scope for archetype evaluation
      console.debug('[DnD][scope for archetype]', scope);

      // Get archetypes and sort by priority
      const archetypes = Object.entries(this.structure.archetypes)
        .map(([key, data]) => ({
          key,
          ...data,
          priority: typeof data.priority === 'number' ? data.priority : 999
        }))
        .sort((a, b) => a.priority - b.priority);

      if (archetypes.length === 0) {
        console.warn('[DndService] No archetypes found in structure');
        return null;
      }

      // Find matching archetype
      for (const arch of archetypes) {
        if (!arch.criteria || typeof arch.criteria !== 'string') {
          console.warn(`[DndService] Invalid criteria for archetype ${arch.key}`);
          continue;
        }

        try {
          if (safeEvaluate(arch.criteria, scope, false)) {
            return arch;
          }
        } catch (error) {
          console.warn(`[DndService] Error evaluating criteria for ${arch.key}:`, error);
          continue;
        }
      }

      // Return wanderer as fallback or first archetype
      return archetypes.find(a => a.key === 'wanderer') || archetypes[0] || null;
    } catch (error) {
      console.error('[DndService] Unexpected error in calculateDndArchetype:', error);
      return null;
    }
  }

  /**
   * Calculate cluster information including DnD archetype
   */
  public calculateCluster(
    scores: Record<string, number>,
    extendedMetrics: Record<string, any> = {}
  ): ClusterInfo {
    try {
      const dndArchetype = this.calculateDndArchetype(scores, extendedMetrics);
      
      if (!dndArchetype) {
        return DEFAULT_CLUSTER;
      }

      return {
        name: dndArchetype.name || DEFAULT_CLUSTER.name,
        description: dndArchetype.description || DEFAULT_CLUSTER.description,
        emoji: dndArchetype.emoji || DEFAULT_CLUSTER.emoji,
        priority: typeof dndArchetype.priority === 'number' 
          ? dndArchetype.priority 
          : DEFAULT_CLUSTER.priority,
        subpersonalities: Array.isArray(dndArchetype.subpersonalities)
          ? dndArchetype.subpersonalities
          : []
      };
    } catch (error) {
      console.error('[DndService] Error in calculateCluster:', error);
      return ERROR_CLUSTER;
    }
  }
}

// Export singleton instance
export const dndService = new DndService();

// Export calculateCluster function for backward compatibility
export const calculateCluster = (
  scores: Record<string, number>,
  extended_metrics: Record<string, any> = {}
): ClusterInfo => dndService.calculateCluster(scores, extended_metrics);

// For testing purposes
export const __test__ = {
  DndService
};
